import config from '../../config';

import callApi from '../../util/apiCaller';
import { slugify } from '../../util/text';
import { storagePersistent as storage } from '../../util/storage';
import { refreshToken as refreshCognitoToken, logout } from '../../util/aws/cognito';

import { sendActivityRecall } from './UserActions';
import { subscribeToChannel, setRedirect, initApp } from '../App/AppActions';
import { switchLanguage } from '../Intl/IntlActions';
import { addError } from '../Error/ErrorActions';

export const LOGIN_USER = 'LOGIN_USER';
export const LOGOUT_USER = 'LOGOUT_USER';

export const USER_AUTH_ACTIVATION_CODE_DELAY = 24 * 60; // in minutes
export const USER_AUTH_VERIFICATION_CODE_DELAY = 5; // in minutes

const USER_AUTH_PREFIX = `${slugify(config.application.name || '').toLowerCase()}.${process.env.NODE_ENV}.auth.`;
export const USER_AUTH_USER_HASH = `${USER_AUTH_PREFIX}user.hash`;
export const USER_AUTH_PROVIDER = `${USER_AUTH_PREFIX}provider`;
export const USER_AUTH_TOKEN_ACCESS = `${USER_AUTH_PREFIX}token.access`;
export const USER_AUTH_TOKEN_REFRESH = `${USER_AUTH_PREFIX}token.refresh`;

export const USER_EMAIL_REGEX = /^(\S+)@(\S+)(\.\w{1,10})+$/m;
export const USER_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$?!%*&_\-#])[A-Za-z\d@$?!%*&_\-#]{12,}$/; // 12 chars + 1 uppercase + 1 number + 1 special character
export const USER_ACTIVITY_THRESHOLD = 5; // in minutes

var activityInterval = null; // eslint-disable-line

export function isLoggedInRequest() {
    return dispatch => {
        if(getAuthAccessToken()) {
            // Never held on an expired session: the application waits on this answer to mount, and
            // the login modal that would release the call only exists once it is mounted
            return callApi('user/getloggeduser', 'get', {}, {}, { neverHeld: true }).then(res => {
                if(res.user) {
                    dispatch(loginUser(res.user));
                    dispatch(switchLanguage(res.user.preferences.language));
                    dispatch(setRedirect());
                    dispatch(subscribeToChannel(`user/${res.user._id}`));
                    // dispatch(sendActivityRecall(res.user));
                    // activityInterval = setInterval(() => dispatch(sendActivityRecall(res.user)), (USER_ACTIVITY_THRESHOLD * 0.9 * 60 * 1000));
                } else {
                    dispatch(logoutUser());
                }
                return res.user;
            }).catch(error => {
                console.error('User: IsLoggedInRequest Error', error);
                if(error && error.message !== 'Unauthorized') {
                    dispatch(addError(error));
                }
                dispatch(logoutUser());
                return false;
            });
        }
        return false;
    };
}

export function checkUserAccountRequest(identifier, isActivation = false, isHash = false) {
    return dispatch => {
        return callApi('user/account/exists', 'post', { identifier, isActivation, isHash }).then(result => {
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function activateAccountRequest(identifier, password, activationCode = '') {
    return dispatch => {
        return callApi('user/account/activation', 'post', { identifier, password, activationCode }).then(result => {
            return result;
        }).catch(err => {
            // TODO: Handle error alert message translations
            console.error(err);
            return null;
        });
    };
}

export function loginUserRequest(identifier, password, verificationKey = '') {
    return dispatch => {
        return callApi('user/login', 'post', { identifier, password, verificationKey }).then(res => {
            res.accessToken && dispatch(loginFromApp(res.accessToken, res.refreshToken));
            if(res.user) {
                dispatch(loginUser(res.user));
                dispatch(switchLanguage(res.user.preferences?.language));
                return res.user;
            }
            return res;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}
export function logoutUserRequest() {
    return dispatch => {
        return callApi('user/logout', 'post', {}).then(res => {
            dispatch(logoutUser(true));
            return res;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function loginByIdRequest(userId) {
    return dispatch => {
        return callApi('user/login/id', 'post', { id: userId }).then(res => {
            res.accessToken && dispatch(loginFromApp(res.accessToken, res.refreshToken));
            if(res.user) {
                dispatch(loginUser(res.user));
                dispatch(switchLanguage(res.user.preferences?.language));
                return res.user;
            }
            return res.user;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function refreshAccessTokenRequest() {
    if(getAuthRefreshToken()) {
        return callApi('user/auth/token/refresh', 'get', null, { Authorization: `Bearer ${getAuthRefreshToken()}` }, { once: true }).then(result => {
            return result?.accessToken;
        });
    }
    return Promise.reject(new Error('NoRefreshTokenSet'));
}

export function validVerificationRequest(identifier, verificationCode) {
    return callApi('user/account/verification', 'post', { identifier, verificationCode });
}

export function forgetPasswordRequest(identifier, verificationKey = '') {
    return dispatch => {
        return callApi('user/account/password/forgot', 'post', { identifier, verificationKey }).then(res => {
            if(res?.identifier && res?.verification) {
                return res;
            }
            res?.ok && dispatch(logoutUser(true));
            return res && res.ok;
        }).catch(error => {
            dispatch(addError(error));
            return null;
        });
    };
}

export function resetAccountPasswordRequest(userId = null) {
    return dispatch => {
        return callApi('user/account/password/reset', 'post', { user: userId }).then(res => {
            res?.ok && dispatch(logoutUser(true));
            return res && res.ok;
        }).catch(error => {
            dispatch(addError(error));
            return null;
        });
    };
}

export function getAuthProvider() {
    return storage && storage.getItem(USER_AUTH_PROVIDER);
}

export function getAuthAccessToken() {
    return storage && storage.getItem(USER_AUTH_TOKEN_ACCESS);
}

export function getAuthRefreshToken() {
    return storage && storage.getItem(USER_AUTH_TOKEN_REFRESH);
}

export function getAuthAccessHeader() {
    const token = getAuthAccessToken();
    if(token) {
        switch(getAuthProvider()) {
            case 'cognito':
                return token;

            case 'app':
            default:
                return `Bearer ${token}`;
        }
    }
    return '';
}

export function setAuthAccessToken(provider, accessToken) {
    if(storage) {
        storage.setItem(USER_AUTH_PROVIDER, provider);
        storage.setItem(USER_AUTH_TOKEN_ACCESS, accessToken);
    } else {
        console.error(new Error('StorageMissingError'));
    }
}

export function loginFromCognito(token, user = null) {
    return dispatch => {
        setAuthAccessToken('cognito', token);
        // dispatch(isLoggedIn());
        dispatch(initApp(false, user));
        return true;
    };
}

export function loginFromApp(accessToken, refreshToken = '') {
    return dispatch => {
        if(storage) {
            storage.setItem(USER_AUTH_PROVIDER, 'app');
            storage.setItem(USER_AUTH_TOKEN_ACCESS, accessToken);
            storage.setItem(USER_AUTH_TOKEN_REFRESH, refreshToken);
        } else {
            console.error(new Error('StorageMissingError'));
        }
        // dispatch(isLoggedIn());
        dispatch(initApp(false));
        return true;
    };
}

export function isLoggedIn() {
    return dispatch => {
        let checkProcess = () => Promise.reject();
        switch(getAuthProvider()) {
            case 'cognito':
                checkProcess = () => refreshCognitoToken(true);
                break;

            case 'app':
                checkProcess = () => Promise.resolve(getAuthAccessToken());
                break;

            default:
        }
        return checkProcess().then(token => {
            return dispatch(isLoggedInRequest());
        }).catch(error => {
            console.error(error);
            // dispatch(logoutUser());
            return dispatch(isLoggedInRequest());
        });
    };
}

export function refreshAccessToken() {
    let refreshProcess = () => Promise.reject(new Error('RefreshTokenProcessNotSet'));
    switch(getAuthProvider()) {
        case 'cognito':
            refreshProcess = () => refreshCognitoToken(true);
            break;

        case 'app':
        default:
            refreshProcess = refreshAccessTokenRequest;
    }
    return refreshProcess().then(token => {
        console.log('refreshAccessToken::: Result', getAuthProvider(), token);
        token && setAuthAccessToken(getAuthProvider(), token);
        return token;
    }).catch(err => {
        console.error('refreshAccessToken::: Fail', err);
        logoutUser();
        return Promise.reject(err);
    });
}

// Getters && Check
export function checkEmail(email) {
    return USER_EMAIL_REGEX.test(email);
}

export function checkPassword(password) {
    return USER_PASSWORD_REGEX.test(password);
}

export function loginUser(user) {
    // console.log('set login user', user);
    // token = token.replace('JWT ', '');
    return {
        type: LOGIN_USER,
        user,
        // token: token
    };
}

export function logoutUser(userAction = false) {
    // if(userAction) {
        let logoutProcess = () => Promise.reject();
        switch(getAuthProvider()) {
            case 'cognito':
                logoutProcess = logout;
                break;

            case 'app':
                console.log('Auth::: App logout');
                logoutProcess = () => Promise.resolve();
                break;

            default:
        }
        logoutProcess().then(result => {
            console.log('Auth: logout process end', storage);
            if(userAction && storage) {
                storage.removeItem(USER_AUTH_PROVIDER);
                storage.removeItem(USER_AUTH_TOKEN_ACCESS);
                storage.removeItem(USER_AUTH_TOKEN_REFRESH);
            }
        }).catch(() => {});
    // }
    clearInterval(activityInterval);
    // The cached exam page carries the server rendered state, so personal data: it must not
    // survive the session on a shared computer. The local answers queue is kept on purpose, so a
    // logout during a service outage does not destroy answers that were never transmitted.
    typeof navigator !== 'undefined' && navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_SESSION_CACHE' });
    return {
        type: LOGOUT_USER,
    };
}
